import type { ReactNode } from 'react';
import Link from 'next/link';

export type PageBackLinkProps = {
  href: string;
  children: ReactNode;
};

/** Breadcrumb-style link above page titles — same on every Communio site. */
export function PageBackLink({ href, children }: PageBackLinkProps) {
  return (
    <p className="site-page__back text-muted">
      <Link href={href}>{children}</Link>
    </p>
  );
}

export type SitePageLayoutProps = {
  children?: ReactNode;
  /** Page h1 — omit only when the body supplies its own title (rare). */
  title?: ReactNode;
  /** Muted subtitle under the title. */
  description?: ReactNode;
  /** Link above the header (e.g. "← All groups"). */
  back?: { href: string; label: string };
  /** Primary CTAs (Create, Join, Submit). */
  actions?: ReactNode;
  /** Sort bars, filters, or controls below the header. */
  toolbar?: ReactNode;
  /** Errors and status messages below the header. */
  alerts?: ReactNode;
};

/**
 * Standard page template for all Communio sites: back link, title, description,
 * actions, toolbar, alerts, then body. Wrap inside the vertical site shell
 * (e.g. paydirt DiscoveryLayout) — do not hand-roll page chrome on new pages.
 */
export function SitePageLayout({
  children,
  title,
  description,
  back,
  actions,
  toolbar,
  alerts,
}: SitePageLayoutProps) {
  const hasHeader = title != null || description != null || actions != null;

  return (
    <>
      {back && <PageBackLink href={back.href}>{back.label}</PageBackLink>}
      {hasHeader && (
        <header className="site-page__header">
          {title != null && <h1 className="site-page__title">{title}</h1>}
          {description != null && (
            <p className="site-page__description text-muted">{description}</p>
          )}
          {actions != null && <div className="site-page__actions">{actions}</div>}
        </header>
      )}
      {toolbar != null && <div className="site-page__toolbar">{toolbar}</div>}
      {alerts}
      {children}
    </>
  );
}
